import React, { createContext, useContext } from "react";
import { collection, query, where, getDocs, updateDoc, doc, serverTimestamp } from "firebase/firestore";
import { db } from "../config/firebase";
import { normalizeBooking, normalizeStatus } from "../utils/booking";
import { createBookingOnServer } from "../utils/createBooking";

/**
 * A second booking API alongside App.context's addBooking. It is mounted in
 * App/index.js but nothing consumes `useBookings()` yet, so it never actually
 * ran — which hid three defects that would have corrupted data the moment it
 * was wired up:
 *
 *   - `addDoc` minted a random document id while the rest of the app treats
 *     `booking.id` as the document id, so the two would disagree.
 *   - it wrote a lowercase `status`, which fails every === 'PENDING'
 *     comparison in the provider dashboard.
 *   - `myBookings` queried `where("userId", ...)`, a field no booking has
 *     ever carried. The field is `uid`. It would always return nothing.
 *
 * Everything here now routes through the same buildBooking/normalizeBooking
 * used by App.context, so this path cannot reintroduce a second schema.
 */

const BookingsContext = createContext(null);

export const BookingsProvider = ({ children }) => {
  /**
   * @param {object} params
   * @param {object} params.listing    a document from the `listings` collection
   * @param {object} params.travelers  { adults, children, infants }
   * @param {Array}  params.dateRange  [start, end]
   * @param {object} params.user       the authenticated Firebase user
   */
  const create = async ({ listing, travelers, dateRange, info = "", user, itineraryId = null }) => {
    // The server writes it, not us. `bookings` is `allow create: if false` for
    // every client — this app included — because price, availability and
    // capacity are all unenforceable from here. We send intent; the server
    // reads the listing, computes the money, checks availability inside a
    // transaction, and writes.
    //
    // buildBooking() is still used for DISPLAY (estimating a total before the
    // traveller commits); it is no longer what gets stored.
    const { bookingId } = await createBookingOnServer({
      listing,
      travelers,
      dateRange,
      info,
      user,
      itineraryId,
    });
    return bookingId;
  };

  const cancel = async (bookingId) => {
    await updateDoc(doc(db, "bookings", bookingId), {
      status: normalizeStatus("cancelled"),
      updatedAt: serverTimestamp(),
    });
  };

  const myBookings = async (userId) => {
    // `uid`, not `userId` — the latter matched nothing.
    const q = query(collection(db, "bookings"), where("uid", "==", userId));
    const snap = await getDocs(q);
    return snap.docs.map((d) => normalizeBooking({ id: d.id, ...d.data() }));
  };

  return (
    <BookingsContext.Provider value={{ create, cancel, myBookings }}>
      {children}
    </BookingsContext.Provider>
  );
};

export const useBookings = () => useContext(BookingsContext);
